import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { EmptyState, LoadingState, Tabs, TabPanel, apiRequest } from '@tpct/admin-ui';
import type { BreadcrumbItem } from '@tpct/admin-ui';
import BreadcrumbBar from '@tpct/mission-core/components/BreadcrumbBar';
import { __ } from '@/hub-shared/utils/i18n';
import type { PortalBootstrapData } from './types/window';
import type { CabinetPageData } from './types/cabinet-page';
import CabinetInfoTab from './tabs/CabinetInfoTab';
import CabinetThemesTab from './tabs/CabinetThemesTab';
import CabinetComplianceTab from './tabs/CabinetComplianceTab';
import SubscriptionTab from './tabs/SubscriptionTab';
import CompanyFilesTab from '@/company-page/src/components/files/CompanyFilesTab';
import CabinetRolesTab from './tabs/CabinetRolesTab';
import type { PortalSearchSyncFn } from './utils/portalUrlState';
import {
  getCabinetProfileEditGate,
  getVisibleCabinetTabIds,
  type CabinetTabId,
} from './gates/portalGates';
import { parsePortalSearchParams, pickAllowedParam } from './utils/portalUrlState';

type CabinetPageProps = {
  bootstrap: PortalBootstrapData;
  onSessionExpired: () => void;
  search?: string;
  patchPortalSearch?: PortalSearchSyncFn;
};

const QUERY_KEY = ['cabinet'] as const;

const CabinetPage = ({ bootstrap, onSessionExpired, search, patchPortalSearch }: CabinetPageProps) => {
  const cabinetId = bootstrap.cabinetId;
  const hasCabinet = cabinetId !== null && cabinetId > 0;

  const params = useMemo(() => parsePortalSearchParams(search ?? ''), [search]);
  const checkoutStatus = useMemo((): 'success' | 'cancelled' | null => {
    const raw = params.get('checkout');
    if (raw === 'success') return 'success';
    if (raw === 'cancelled') return 'cancelled';
    return null;
  }, [params]);
  const { data, isLoading, isError } = useQuery<CabinetPageData>({
    queryKey: QUERY_KEY,
    queryFn: () => apiRequest<CabinetPageData>('cabinet', {}, 'AccountingHubPortal'),
    enabled: hasCabinet,
  });
  const selfFileStoreOwnerId = data?.self_file_store_owner_id ?? null;
  const visibleTabIds = useMemo(
    () => getVisibleCabinetTabIds(bootstrap, selfFileStoreOwnerId),
    [bootstrap, selfFileStoreOwnerId],
  );
  const activeTab = pickAllowedParam(params.get('tab'), visibleTabIds, 'info');
  const portalTabs = typeof patchPortalSearch === 'function';

  useEffect(() => {
    // The files tab depends on the cabinet answer: judge the URL once it is in.
    if (!patchPortalSearch || data === undefined) {
      return;
    }
    const rawTab = params.get('tab');
    const rawEditRole = params.get('editRole');
    const tabForbidden = rawTab !== null && rawTab !== '' && !visibleTabIds.includes(rawTab as CabinetTabId);
    const rolesForbidden = rawEditRole !== null && rawEditRole !== '' && !visibleTabIds.includes('roles');
    if (tabForbidden || rolesForbidden) {
      patchPortalSearch({ tab: null, editRole: null }, 'replace');
    }
  }, [data, params, patchPortalSearch, visibleTabIds]);

  const cabinetProfileEditGate = useMemo(
    () => getCabinetProfileEditGate({
      canEditCabinet: data?.can_edit_cabinet,
      isCabinetOwner: bootstrap.isCabinetOwner,
    }),
    [bootstrap.isCabinetOwner, data?.can_edit_cabinet],
  );

  if (!hasCabinet) {
    return (
      <EmptyState
        title={__('Aucun cabinet associé')}
        description={__(
          "Ce compte n\u2019est pas rattaché à un cabinet. Connectez-vous avec un compte cabinet ou souscrivez un abonnement.",
        )}
      />
    );
  }

  if (isLoading) {
    return <LoadingState columns={[__('Champ'), __('Valeur')]} className="cabinet-page-loading" />;
  }

  if (isError || !data) {
    return (
      <EmptyState
        title={__('Erreur de chargement')}
        description={__('Impossible de charger les données du cabinet.')}
      />
    );
  }

  const breadcrumbItems: BreadcrumbItem[] = [{ label: __('Mon cabinet') }];

  const onTabChange = (tabId: string): void => {
    if (!patchPortalSearch) {
      return;
    }
    const next = tabId as CabinetTabId;
    if (!visibleTabIds.includes(next)) {
      return;
    }
    patchPortalSearch({ tab: next, editRole: null }, 'push');
  };

  const cabinetTabPanels = [
    <TabPanel key="info" id="info" label={__('Informations')}>
      <CabinetInfoTab
        data={data}
        queryKey={[...QUERY_KEY]}
        bootstrapKey="AccountingHubPortal"
        cabinetProfileEditGate={cabinetProfileEditGate}
      />
    </TabPanel>,
    <TabPanel key="themes" id="themes" label={__('Thème et logo')}>
      <CabinetThemesTab
        data={data}
        queryKey={[...QUERY_KEY]}
        bootstrapKey="AccountingHubPortal"
        cabinetProfileEditGate={cabinetProfileEditGate}
      />
    </TabPanel>,
    <TabPanel key="compliance" id="compliance" label={__('Contrôle et conformité')}>
      <CabinetComplianceTab
        cabinetId={data.id}
        bootstrap={bootstrap}
        bootstrapKey="AccountingHubPortal"
        onSessionExpired={onSessionExpired}
      />
    </TabPanel>,
  ];

  if (selfFileStoreOwnerId !== null && visibleTabIds.includes('files')) {
    cabinetTabPanels.push(
      <TabPanel key="files" id="files" label={__('Fichiers')}>
        {/* The dossier « Fichiers » tab, on the cabinet's own store (an `acchub_entity`, not a dossier). */}
        <CompanyFilesTab companyId={selfFileStoreOwnerId} bootstrapKey="AccountingHubPortal" />
      </TabPanel>,
    );
  }

  if (visibleTabIds.includes('subscription')) {
    cabinetTabPanels.push(
      <TabPanel key="subscription" id="subscription" label={__('Abonnement')}>
        <SubscriptionTab
          bootstrap={bootstrap}
          onSessionExpired={onSessionExpired}
          checkoutStatus={checkoutStatus}
          patchPortalSearch={portalTabs ? patchPortalSearch : undefined}
        />
      </TabPanel>,
    );
  }

  if (visibleTabIds.includes('roles')) {
    cabinetTabPanels.push(
      <TabPanel key="roles" id="roles" label={__('Permissions')}>
        <CabinetRolesTab
          bootstrap={bootstrap}
          cabinetId={data.id}
          search={search}
          patchPortalSearch={patchPortalSearch}
        />
      </TabPanel>,
    );
  }

  return (
    <div className="cabinet-page">
      <BreadcrumbBar items={breadcrumbItems} />
      {portalTabs ? (
        <Tabs activeTab={activeTab} defaultActiveTab="info" onActiveTabChange={onTabChange}>
          {cabinetTabPanels}
        </Tabs>
      ) : (
        <Tabs defaultActiveTab="info">{cabinetTabPanels}</Tabs>
      )}
    </div>
  );
};

export default CabinetPage;
